import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { Miko } from "../components/miko/Miko";
import { Bell } from "../components/omikuji/Bell";
import { KujiBox } from "../components/omikuji/KujiBox";
import { OmikujiCard } from "../components/omikuji/OmikujiCard";
import { StartOverlay } from "../components/ui/StartOverlay";
import { useSound } from "../hooks/useSound";
import { useShake } from "../hooks/useShake";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";
import { drawOmikuji, type DrawResult, RARITY_DISPLAY } from "../lib/omikuji";
import { appendDraw, getTodaySoloDraw } from "../lib/storage";

/**
 * §2.2 1人モード (短縮版・約20秒)
 *  miko (3s) → bell (待機) → box+stick (3s) → card open (3s) → result (5s) → collect (3s)
 *
 * 1日1回ガード: localStorage で今日の solo draw を検索し、あれば表示専用画面を出す。
 */

type Step = "miko" | "shake" | "stick" | "open" | "result" | "collect";
const ORDER: Step[] = ["miko", "shake", "stick", "open", "result", "collect"];

const AUTO: Partial<Record<Step, number>> = {
  miko: 3000,
  stick: 3000,
  open: 2500,
  result: 4500,
  collect: 2500,
};

type State = { step: Step; cardState: "closed" | "opening" | "opened" };
function reducer(s: State, a: { type: "advance" } | { type: "card"; v: State["cardState"] }): State {
  if (a.type === "advance") {
    const i = ORDER.indexOf(s.step);
    return { ...s, step: ORDER[Math.min(i + 1, ORDER.length - 1)] };
  }
  return { ...s, cardState: a.v };
}

export function SoloDraw() {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const reduce = useReducedMotion();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();
  const shake = useShake({ goal: 5 });

  const [existing, setExisting] = useState<ReturnType<typeof getTodaySoloDraw>>();
  useEffect(() => {
    setExisting(getTodaySoloDraw());
  }, []);

  // 結果は1度だけ抽選
  const result = useMemo<DrawResult>(() => drawOmikuji(), []);
  const [state, dispatch] = useReducer(reducer, { step: "miko", cardState: "closed" });
  const [started, setStarted] = useState(false);
  const startedRef = useRef(false);
  const savedRef = useRef(false);

  // shake 完了 → stick へ
  useEffect(() => {
    if (state.step === "shake" && shake.completed) {
      play("kirari");
      vibrate(VibrationPatterns.bellShake);
      const id = window.setTimeout(() => dispatch({ type: "advance" }), 400);
      return () => clearTimeout(id);
    }
  }, [state.step, shake.completed, play, vibrate]);

  // 自動進行
  useEffect(() => {
    const dur = reduce ? 600 : AUTO[state.step];
    if (!dur) return;
    const id = window.setTimeout(() => dispatch({ type: "advance" }), dur);
    return () => clearTimeout(id);
  }, [state.step, reduce]);

  // ステップ毎の効果音/振動
  useEffect(() => {
    switch (state.step) {
      case "stick":
        play("woodTap");
        vibrate(VibrationPatterns.woodTap);
        break;
      case "open":
        dispatch({ type: "card", v: "opening" });
        play("cardOpen");
        vibrate(VibrationPatterns.cardOpen);
        window.setTimeout(() => dispatch({ type: "card", v: "opened" }), 600);
        if (result.rarity === "dai_kichi" || result.rarity === "tokudai_kichi") {
          window.setTimeout(() => {
            play("taikoDon");
            vibrate(VibrationPatterns.daiKichi);
          }, 800);
        }
        break;
      case "collect":
        play("kirari");
        if (!savedRef.current) {
          savedRef.current = true;
          appendDraw({
            mode: "solo",
            rarity: result.rarity,
            bodyText: result.bodyText,
            bodyTextEn: result.bodyTextEn,
            themeAuto: result.themeAuto,
          });
        }
        break;
    }
  }, [state.step, play, vibrate, result]);

  useEffect(() => {
    if (state.step === "collect") {
      const id = window.setTimeout(() => navigate("/home"), AUTO.collect);
      return () => clearTimeout(id);
    }
  }, [state.step, navigate]);

  function startSequence() {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarted(true);
    unlock();
    void shake.requestPermission();
  }

  // ====== 既に今日引いていた場合: 表示専用 ======
  if (existing) {
    const rd = RARITY_DISPLAY[existing.rarity];
    return (
      <main className="relative min-h-dvh bg-[var(--color-night-deep)] text-[var(--color-washi)] flex flex-col items-center justify-center px-6 py-12 overflow-hidden">
        <Sakura count={8} />
        <p
          className="text-xs tracking-[0.3em] text-[var(--color-gold-light)] mb-4"
          style={{ fontFamily: "var(--font-fude)" }}
        >
          {t("solo.todaysCard")}
        </p>
        <OmikujiCard
          rarity={existing.rarity}
          body={i18n.language === "en" ? existing.bodyTextEn : existing.bodyText}
          state="opened"
          size={200}
        />
        <p
          className="mt-8 text-base tracking-widest"
          style={{ fontFamily: "var(--font-fude)", color: rd.color }}
        >
          {t("solo.alreadyDrawn")}
        </p>
        <p className="text-[10px] opacity-50 mt-2">{t("solo.alreadyDrawnHint")}</p>
        <Link
          href="/home"
          className="mt-8 text-sm text-[var(--color-gold-light)] underline-offset-4 hover:underline"
        >
          ← {t("common.back")}
        </Link>
      </main>
    );
  }

  // ====== 通常フロー ======
  return (
    <main className="relative min-h-dvh overflow-hidden bg-[var(--color-night-deep)] text-[var(--color-washi)] select-none">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 80%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 50%, #1a0c12 100%)",
        }}
      />
      <Sakura count={10} />

      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center px-4 py-8">
        {/* ヒント */}
        <AnimatePresence mode="wait">
          {state.step === "shake" && started && (
            <motion.p
              key="hint-shake"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.85 }}
              exit={{ opacity: 0 }}
              className="absolute top-[10%] inset-x-0 text-center text-sm tracking-[0.2em] text-[var(--color-gold-light)]"
              style={{ fontFamily: "var(--font-fude)" }}
            >
              {t("ritual.shakeBell")}
            </motion.p>
          )}
        </AnimatePresence>

        {/* miko 登場 */}
        <AnimatePresence>
          {state.step === "miko" && (
            <motion.div
              key="miko"
              initial={{ opacity: 0, y: 60, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.0 }}
            >
              <Miko size={220} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* bell */}
        <AnimatePresence>
          {state.step === "shake" && (
            <motion.div
              key="bell"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-4"
            >
              <Bell
                intensity={shake.intensity}
                onTap={() => {
                  shake.tap();
                  play("bellTap");
                  vibrate(VibrationPatterns.bellTap);
                }}
                size={200}
              />
              <div className="w-32 h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-[var(--color-gold)]"
                  animate={{ width: `${shake.intensity * 100}%` }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* box + stick */}
        <AnimatePresence>
          {state.step === "stick" && (
            <motion.div
              key="box"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
            >
              <KujiBox stickOut size={150} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* card */}
        <AnimatePresence>
          {(["open", "result", "collect"] as Step[]).includes(state.step) && (
            <motion.div
              key="card"
              initial={{ opacity: 0, y: 40 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: state.step === "collect" ? 0.5 : 1,
              }}
              transition={{ duration: 0.8 }}
            >
              <OmikujiCard
                rarity={result.rarity}
                body={i18n.language === "en" ? result.bodyTextEn : result.bodyText}
                state={state.cardState}
                size={200}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* collect notice */}
        <AnimatePresence>
          {state.step === "collect" && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-[15%] inset-x-0 text-center text-sm tracking-widest text-[var(--color-gold-light)]"
              style={{ fontFamily: "var(--font-fude)" }}
            >
              ✦ {t("ritual.added")} ✦
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {!started && (
          <StartOverlay title={t("solo.title")} hint={t("solo.shakeToBegin")} onStart={startSequence} />
        )}
      </AnimatePresence>
    </main>
  );
}
